import type { CourseProgress } from "@/core/progress";
import {
  flattenLessons,
  lessonKey,
  stepKey,
  type Course,
  type CourseModule,
  type Lesson,
} from "@/core/types";
import type { ProgressLookup } from "@/ui/components/Progress";

/**
 * Kennzahlen zum Kursfortschritt – gemeinsam für das Seitenpanel und die
 * Browser-Ansicht. Alles wird aus den erledigten Schritten abgeleitet, keine
 * Zahl wird getrennt gespeichert.
 */

/** Abfragen auf einem gespeicherten Fortschritt, ohne den Hook des Panels. */
export function lookupFor(progress: CourseProgress): ProgressLookup {
  const done = new Set(progress.completedSteps);
  return {
    isStepDone: (moduleId, lessonId, stepId) => done.has(stepKey(moduleId, lessonId, stepId)),
    completedInLesson: (moduleId, lessonId, stepIds) =>
      stepIds.filter((id) => done.has(stepKey(moduleId, lessonId, id))).length,
  };
}

export function lessonStepsDone(module: CourseModule, lesson: Lesson, lookup: ProgressLookup) {
  return lookup.completedInLesson(
    module.id,
    lesson.id,
    lesson.steps.map((step) => step.id),
  );
}

export function isLessonDone(module: CourseModule, lesson: Lesson, lookup: ProgressLookup) {
  return lessonStepsDone(module, lesson, lookup) === lesson.steps.length;
}

export function moduleMinutes(module: CourseModule): number {
  return module.lessons.reduce((sum, lesson) => sum + lesson.durationMinutes, 0);
}

export interface CourseStats {
  stepsDone: number;
  stepsTotal: number;
  lessonsDone: number;
  lessonsTotal: number;
  modulesDone: number;
  modulesTotal: number;
  /** Summe der Dauer aller abgeschlossenen Lektionen. */
  minutesDone: number;
  minutesTotal: number;
  /** Erledigte Schritte in Prozent, ganzzahlig. */
  percentage: number;
}

export function courseStats(course: Course, lookup: ProgressLookup): CourseStats {
  const stats: CourseStats = {
    stepsDone: 0,
    stepsTotal: 0,
    lessonsDone: 0,
    lessonsTotal: 0,
    modulesDone: 0,
    modulesTotal: course.modules.length,
    minutesDone: 0,
    minutesTotal: 0,
    percentage: 0,
  };

  for (const module of course.modules) {
    let moduleComplete = true;
    for (const lesson of module.lessons) {
      const done = lessonStepsDone(module, lesson, lookup);
      stats.stepsDone += done;
      stats.stepsTotal += lesson.steps.length;
      stats.lessonsTotal += 1;
      stats.minutesTotal += lesson.durationMinutes;
      if (done === lesson.steps.length) {
        stats.lessonsDone += 1;
        stats.minutesDone += lesson.durationMinutes;
      } else {
        moduleComplete = false;
      }
    }
    if (moduleComplete) stats.modulesDone += 1;
  }

  stats.percentage =
    stats.stepsTotal === 0 ? 0 : Math.round((stats.stepsDone / stats.stepsTotal) * 100);
  return stats;
}

/** Summiert die Kennzahlen mehrerer Kurse; der Prozentwert kommt aus den Summen. */
export function sumStats(all: CourseStats[]): CourseStats {
  const total = all.reduce<CourseStats>(
    (sum, stats) => ({
      stepsDone: sum.stepsDone + stats.stepsDone,
      stepsTotal: sum.stepsTotal + stats.stepsTotal,
      lessonsDone: sum.lessonsDone + stats.lessonsDone,
      lessonsTotal: sum.lessonsTotal + stats.lessonsTotal,
      modulesDone: sum.modulesDone + stats.modulesDone,
      modulesTotal: sum.modulesTotal + stats.modulesTotal,
      minutesDone: sum.minutesDone + stats.minutesDone,
      minutesTotal: sum.minutesTotal + stats.minutesTotal,
      percentage: 0,
    }),
    {
      stepsDone: 0,
      stepsTotal: 0,
      lessonsDone: 0,
      lessonsTotal: 0,
      modulesDone: 0,
      modulesTotal: 0,
      minutesDone: 0,
      minutesTotal: 0,
      percentage: 0,
    },
  );
  total.percentage =
    total.stepsTotal === 0 ? 0 : Math.round((total.stepsDone / total.stepsTotal) * 100);
  return total;
}

export interface LessonRef {
  module: CourseModule;
  lesson: Lesson;
}

/**
 * Die nächste Lektion: die zuletzt geöffnete, solange sie offen ist, sonst die
 * nächste offene danach im Lehrplan, sonst die erste offene überhaupt.
 */
export function findNextLesson(
  course: Course,
  lookup: ProgressLookup,
  lastLessonKey: string | undefined,
): LessonRef | null {
  const lessons = flattenLessons(course);
  const isOpen = ({ module, lesson }: LessonRef) => !isLessonDone(module, lesson, lookup);

  const lastIndex = lessons.findIndex(
    ({ module, lesson }) => lessonKey(module.id, lesson.id) === lastLessonKey,
  );
  if (lastIndex !== -1) {
    const afterLast = lessons.slice(lastIndex).find(isOpen);
    if (afterLast) return afterLast;
  }
  return lessons.find(isOpen) ?? null;
}

/**
 * Abgeschlossene Lektionen, die zuletzt erledigte zuerst. Die Reihenfolge
 * stammt aus der Liste der erledigten Schritte, die beim Erledigen wächst.
 */
export function recentlyCompleted(course: Course, progress: CourseProgress, limit: number) {
  const position = new Map(progress.completedSteps.map((key, index) => [key, index]));
  const lookup = lookupFor(progress);
  return flattenLessons(course)
    .filter(({ module, lesson }) => isLessonDone(module, lesson, lookup))
    .map((ref) => ({
      ...ref,
      order: Math.max(
        ...ref.lesson.steps.map(
          (step) => position.get(stepKey(ref.module.id, ref.lesson.id, step.id)) ?? -1,
        ),
      ),
    }))
    .sort((a, b) => b.order - a.order)
    .slice(0, limit);
}

/** Module, in denen schon etwas erledigt ist, die aber noch nicht fertig sind. */
export function modulesInProgress(course: Course, lookup: ProgressLookup) {
  return course.modules
    .map((module) => {
      let done = 0;
      let total = 0;
      for (const lesson of module.lessons) {
        done += lessonStepsDone(module, lesson, lookup);
        total += lesson.steps.length;
      }
      return { module, done, total };
    })
    .filter(({ done, total }) => done > 0 && done < total);
}

/** „45 Min.“, „2 Std.“ oder „3 Std. 20 Min.“ */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} Std.` : `${hours} Std. ${rest} Min.`;
}
